import React from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Trash2, Copy, Loader2, Users, ShieldCheck, Tag } from 'lucide-react';
import { FIELD_TYPE_ICON } from './fieldTypeIcons';
import { orderedFields } from '../../utils/checkSets';

// One check set as a card: its name, what it asks for (each field with its
// type icon, unit and mandatory mark), its two switches as badges, and the
// actions this viewer has on it. `copyLabel` names where a copy goes (the
// admin copies into a doctor's sets, not "my sets").
function CheckSetCard({ set, onEdit, onDelete, onCopy, copyLabel = null, busy = false, showMeantFor = false }) {
    const { t } = useTranslation();
    const fields = orderedFields(set);
    return (
        <article data-testid={`check-set-card-${set.setId}`} className="flex flex-col gap-3 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm transition-colors hover:border-[#4ABDBD]/40">
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <h4 data-testid={`check-set-card-name-${set.setId}`} className="truncate text-sm font-bold text-gray-800">{set.name}</h4>
                    <p className="mt-0.5 text-[11px] text-gray-400">
                        {t('checkSets.fields_count', { count: fields.length })}
                        {set.version > 1 ? ` · ${t('checkSets.badge_version', { version: set.version })}` : ''}
                    </p>
                </div>
                <div className="flex shrink-0 items-center gap-0.5">
                    {onCopy && (
                        <button
                            type="button"
                            data-testid={`check-set-copy-${set.setId}`}
                            onClick={() => onCopy(set)}
                            disabled={busy}
                            className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-[#2D8A8A] hover:bg-[#4ABDBD]/10 disabled:opacity-50"
                        >
                            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <Copy className="h-3.5 w-3.5" aria-hidden="true" />}
                            {copyLabel || t('checkSets.copy')}
                        </button>
                    )}
                    {onEdit && (
                        <button
                            type="button"
                            data-testid={`check-set-edit-${set.setId}`}
                            onClick={() => onEdit(set)}
                            disabled={busy}
                            aria-label={t('checkSets.edit')}
                            title={t('checkSets.edit')}
                            className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-[#2D8A8A] disabled:opacity-40"
                        >
                            <Pencil className="h-4 w-4" aria-hidden="true" />
                        </button>
                    )}
                    {onDelete && (
                        <button
                            type="button"
                            data-testid={`check-set-delete-${set.setId}`}
                            onClick={() => onDelete(set)}
                            disabled={busy}
                            aria-label={t('checkSets.delete')}
                            title={t('checkSets.delete')}
                            className="rounded-lg p-1.5 text-gray-500 hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                        >
                            {busy ? <Loader2 className="h-4 w-4 animate-spin text-red-500" aria-hidden="true" /> : <Trash2 className="h-4 w-4" aria-hidden="true" />}
                        </button>
                    )}
                </div>
            </div>

            <ul className="flex flex-wrap gap-1.5" data-testid={`check-set-card-fields-${set.setId}`}>
                {fields.map((f) => {
                    const Icon = FIELD_TYPE_ICON[f.type] || FIELD_TYPE_ICON.text;
                    return (
                        <li key={f.key} className="inline-flex items-center gap-1 rounded-lg border border-gray-100 bg-gray-50 px-2 py-0.5 text-[11px] text-gray-600">
                            <Icon className="h-3 w-3 text-[#2D8A8A]" aria-hidden="true" />
                            {f.label}
                            {f.unit ? <span className="text-gray-400">({f.unit})</span> : null}
                            {f.required ? <span className="text-red-500" aria-label={t('checkSets.field_required')}>*</span> : null}
                        </li>
                    );
                })}
            </ul>

            {(set.staffMayRecord || set.requiredEveryVisit || (showMeantFor && set.meantFor)) && (
                <div className="flex flex-wrap gap-1.5">
                    {showMeantFor && set.meantFor && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600">
                            <Tag className="h-3 w-3" aria-hidden="true" />
                            {t('checkSets.badge_meant_for', { label: set.meantFor })}
                        </span>
                    )}
                    {set.staffMayRecord && (
                        <span data-testid={`check-set-badge-staff-${set.setId}`} className="inline-flex items-center gap-1 rounded-full bg-[#4ABDBD]/10 px-2 py-0.5 text-[10px] font-semibold text-[#2D8A8A]">
                            <Users className="h-3 w-3" aria-hidden="true" />
                            {t('checkSets.badge_staff')}
                        </span>
                    )}
                    {set.requiredEveryVisit && (
                        <span data-testid={`check-set-badge-required-${set.setId}`} className="inline-flex items-center gap-1 rounded-full bg-[#D7A05C]/15 px-2 py-0.5 text-[10px] font-semibold text-[#9a6a2c]">
                            <ShieldCheck className="h-3 w-3" aria-hidden="true" />
                            {t('checkSets.badge_required')}
                        </span>
                    )}
                </div>
            )}
        </article>
    );
}

export default CheckSetCard;
